import React from 'react';
import { useTranslation } from 'react-i18next';

import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../../utils/variables';
import { ElementObj } from '../../../../../viewer/elements/element-obj';
import { publish, SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../../../viewer/project-geodata-manager';
import { useSharedView } from '../../../../hooks/use-shared-view';
import { nameof } from '../../../../utils/nameof';
import { Chip } from '../../../chip/chip';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Select } from '../../../select/select';

type Props = {
  asset: ElementObj;
};

enum Status {
  Idle,
  Saving,
}

const TimelineSection = React.memo((props: Props) => {
  const { t } = useTranslation();
  const { isSharedView } = useSharedView();

  const [status, setStatus] = React.useState(Status.Idle);
  const [timelineId, setTimelineId] = React.useState(props.asset.timelineContainerId);

  const timelines = ProjectGeodataManager.instance().getDetailedTimelineItems();
  const timeline = timelines.find((x) => x.id === timelineId);

  const updatePoiTimelineContainer = async (timelineContainerId?: string) => {
    const poiService = SkyMapAxiosServiceFactory.instance.createPoiServiceV1();
    if (isDefined(timelineContainerId)) {
      await poiService.associateTimelineContainerWithPoi({
        path: { poiId: props.asset.assetId!, timelineContainerId: timelineContainerId },
      });
    } else {
      await poiService.removeAssociatedTimelineContainerFromPoi({
        path: { poiId: props.asset.assetId! },
      });
    }
  };

  const saveTimelineId = async (value: string | undefined) => {
    const prevTimelineContainerId = props.asset.timelineContainerId;
    try {
      setStatus(Status.Saving);
      setTimelineId(value);
      props.asset.timelineContainerId = isDefined(value) ? value : null;
      if (props.asset.assetType === 'poi') {
        await updatePoiTimelineContainer(value);
      } else {
        await props.asset.save();
      }
    } catch (err) {
      setTimelineId(prevTimelineContainerId);
      props.asset.timelineContainerId = prevTimelineContainerId;
      displayErrorNotification(value === undefined ? 'disconnect' : 'connect');
    } finally {
      setStatus(Status.Idle);
    }
  };

  const displayErrorNotification = (requestType: 'connect' | 'disconnect') => {
    const resolveMessageFromAssetType = (): string => {
      if (requestType === 'connect') {
        switch (props.asset.assetType) {
          case 'area':
            return t('timeline.connectFailedMessage.area', { ns: 'skyviewAssetMenu' });
          case 'line':
            return t('timeline.connectFailedMessage.line', { ns: 'skyviewAssetMenu' });
          case 'dxf':
          case 'glb':
            return t('timeline.connectFailedMessage.cad', { ns: 'skyviewAssetMenu' });
          case 'model':
            return t('timeline.connectFailedMessage.model', { ns: 'skyviewAssetMenu' });
          case 'poi':
            return t('timeline.connectFailedMessage.poi', { ns: 'skyviewAssetMenu' });
        }
      }

      switch (props.asset.assetType) {
        case 'area':
          return t('timeline.disconnectFailedMessage.area', { ns: 'skyviewAssetMenu' });
        case 'line':
          return t('timeline.disconnectFailedMessage.line', { ns: 'skyviewAssetMenu' });
        case 'dxf':
        case 'glb':
          return t('timeline.disconnectFailedMessage.cad', { ns: 'skyviewAssetMenu' });
        case 'model':
          return t('timeline.disconnectFailedMessage.model', { ns: 'skyviewAssetMenu' });
        case 'poi':
          return t('timeline.disconnectFailedMessage.poi', { ns: 'skyviewAssetMenu' });
      }

      throw new Error('Unknown asset type');
    };

    publish(SubscriptionTopic.ToastrMessage, {
      type: 'error',
      message: resolveMessageFromAssetType(),
    });
  };

  return (
    <LabelledContainer text={t('timeline.sectionName', { ns: 'skyviewAssetMenu' })}>
      {timeline ? (
        <Chip
          deleteIcon={
            status === Status.Saving ? { icon: ['fal', 'spinner'], spin: true } : undefined
          }
          disabled={status === Status.Saving}
          leftArea={{
            backgroundColor: '#EEE',
            text: `#${timeline.number}`,
          }}
          text={timeline.dateFormatted}
          onDelete={
            isSharedView
              ? undefined
              : async () => {
                  await saveTimelineId(undefined);
                }
          }
        />
      ) : !isSharedView ? (
        <Select
          disabled={isSharedView}
          options={timelines.map((x) => ({ id: x.id, name: x.dateFormattedWithNumber }))}
          padding={0.2}
          placeholderText={t('timeline.dropDown.none', { ns: 'skyviewAssetMenu' })}
          onChange={async (e) => {
            await saveTimelineId(e.target.value);
          }}
        />
      ) : (
        '-'
      )}
    </LabelledContainer>
  );
});

TimelineSection.displayName = nameof({ TimelineSection });

export { TimelineSection, Props as TimelineSectionProps };
